import { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Alert,
} from '@mui/material';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { TranslationContext } from '../Context/TranslationContext';
import { useTranslation } from '../hooks/useTranslation';
import { LOCALE_BY_LANG } from '../Utils/i18nHelpers';
import { listOrders } from '../Utils/ordersApi';

function formatDate(dateStr, locale) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(locale);
}

function formatMontant(amountCents, currency, locale) {
    if (amountCents == null) return '—';
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: (currency || 'eur').toUpperCase(),
    }).format(amountCents / 100);
}

export default function OrdersPage() {
    const t = useTranslation('Orders.json');
    const { currentLanguage } = useContext(TranslationContext);
    const locale = LOCALE_BY_LANG[currentLanguage] || LOCALE_BY_LANG.fr;
    const STATUS_CONFIG = {
        pending: { label: t('orders.status.pending'), color: 'default' },
        paid: { label: t('orders.status.paid'), color: 'success' },
        active: { label: t('orders.status.active'), color: 'success' },
        canceled: { label: t('orders.status.canceled'), color: 'default' },
        expired: { label: t('orders.status.expired'), color: 'warning' },
        failed: { label: t('orders.status.failed'), color: 'error' },
    };
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        listOrders()
            .then(setOrders)
            .catch(() => setError(t('orders.error')))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <ShoppingCartOutlinedIcon sx={{ color: '#FF7233', fontSize: 32 }} />
                <Typography variant="h5" fontWeight={600}>
                    {t('orders.title')}
                </Typography>
            </Box>

            {loading && (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress sx={{ color: '#FF7233' }} />
                </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && orders.length === 0 && (
                <Alert severity="info">{t('orders.empty')}</Alert>
            )}

            {!loading && !error && orders.length > 0 && (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><Typography fontWeight={600}>{t('orders.column.application')}</Typography></TableCell>
                                <TableCell><Typography fontWeight={600}>{t('orders.column.type')}</Typography></TableCell>
                                <TableCell><Typography fontWeight={600}>{t('orders.column.status')}</Typography></TableCell>
                                <TableCell><Typography fontWeight={600}>{t('orders.column.amount')}</Typography></TableCell>
                                <TableCell><Typography fontWeight={600}>{t('orders.column.date')}</Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.map((order) => {
                                const status = STATUS_CONFIG[order.status] || { label: order.status, color: 'default' };
                                return (
                                    <TableRow key={order.id} hover>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {order.application_name || order.application_client_id}
                                                {order.application_option_id ? ` (${t('orders.option')})` : ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {order.type === 'subscription' ? t('orders.type.subscription') : t('orders.type.one_time')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={status.label} color={status.color} size="small" />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatMontant(order.amount, order.currency, locale)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{formatDate(order.created_at, locale)}</Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
